import { useState } from "react";
import { useDispatch } from "react-redux";
import CustomInput from "../../../../Components/UI/Inputs/CustomInput";
import BoltButton from "../../../../Components/UI/Buttons/BoltButton";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Input, Typography } from "@mui/material";
import {
  updateBalance,
  subtractBalance,
} from "../../../../services/AdminServices/Users/users.services";
import { showSnackBar } from "../../../../store/common/snackBarSlice";
import { useTranslation } from "react-i18next";

const BalanceManagement = ({ userData, shipperData, updateShipper }) => {
  const { t } = useTranslation("admin");
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("add");
  const [addBalanceData, setAddBalanceData] = useState({
    balance: "",
    notes: "",
    payment_type: "by_hand",
  });
  const [subtractBalanceData, setSubtractBalanceData] = useState({
    balance: "",
    deduction_reason: "",
    reasonFile: "",
  });
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  if (!shipperData) {
    return null;
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange({ target: { files: [file] } });
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddBalanceData({ ...addBalanceData, [name]: value });
  };

  const handleSubtractChange = (e) => {
    const { name, value } = e.target;
    setSubtractBalanceData({ ...subtractBalanceData, [name]: value });
  };

  const handleAddFieldChange = (name, value) => {
    setAddBalanceData({ ...addBalanceData, [name]: value });
  };

  const handleAddSave = async () => {
    if (!addBalanceData.balance || !addBalanceData.notes) {
      dispatch(
        showSnackBar({
          text: t("controls.pleaseFillAllRequiredFields"),
          severity: "error",
        })
      );
      return;
    }

    setLoading(true);
    const id = shipperData?.id;
    const response = await updateBalance(addBalanceData, id);

    if (response.error) {
      dispatch(
        showSnackBar({
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        })
      );
    } else {
      dispatch(
        showSnackBar({
          text: t("controls.balanceUpdatedSuccessfully"),
          severity: "success",
        })
      );

      const newBalance = response?.data?.result?.balance;

      // ✅ Update parent user state
      if (updateShipper && typeof newBalance !== "undefined") {
        updateShipper(id, "balance", newBalance);
      }
    }

    setAddBalanceData({ balance: "", notes: "", payment_type: "by_hand" });
    setLoading(false);
  };

  const handleSubtractSave = async () => {
    if (!subtractBalanceData.balance || !subtractBalanceData.deduction_reason) {
      dispatch(
        showSnackBar({
          text: t("controls.pleaseFillAllRequiredFields"),
          severity: "error",
        })
      );
      return;
    }

    setLoading(true);
    const Formdata = new FormData();
    Formdata.append("balance", subtractBalanceData.balance);
    Formdata.append("deduction_reason", subtractBalanceData.deduction_reason);
    if (subtractBalanceData.reasonFile !== "") {
      Formdata.append("reasonFile", subtractBalanceData.reasonFile);
    }

    const id = shipperData?.id;
    const response = await subtractBalance(Formdata, id);

    if (response.error) {
      dispatch(
        showSnackBar({
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        })
      );
    } else {
      dispatch(
        showSnackBar({
          text: t("controls.balanceUpdatedSuccessfully"),
          severity: "success",
        })
      );

      const newBalance = response?.data?.result?.balance;

      // ✅ Update parent user state
      if (updateShipper && typeof newBalance !== "undefined") {
        updateShipper(id, "balance", newBalance);
      }
    }

    setSubtractBalanceData({
      balance: "",
      deduction_reason: "",
      reasonFile: "",
    });
    setLoading(false);
  };

  const handleFileChange = (e) => {
    setSubtractBalanceData({
      ...subtractBalanceData,
      reasonFile: e.target.files[0],
    });
  };

  const renderAddBalanceTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CustomInput
          label={t("controls.account")}
          value={userData?.username || t("controls.demoUser")}
          readOnly
        />
        <div className="space-y-2">
          <label className="block text-sm font-medium text-ui-text mb-2">
            {t("controls.paymentType")}
          </label>
          <Autocomplete
            disablePortal
            options={["by_hand", "bank_transfer", "cheque"]}
            getOptionLabel={(option) =>
              option.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
            }
            value={addBalanceData.payment_type}
            onChange={(e, payment_type) =>
              handleAddFieldChange("payment_type", payment_type)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("controls.paymentType")}
                className="w-full"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "48px",
                    borderRadius: "8px",
                  },
                }}
              />
            )}
          />
        </div>
      </div>

      <CustomInput
        label={t("controls.instructions")}
        value={addBalanceData.notes}
        onChange={handleAddChange}
        name="notes"
        placeholder={t("controls.enterInstructions")}
        required
      />

      <CustomInput
        label={t("controls.amount")}
        type="number"
        value={addBalanceData.balance}
        onChange={handleAddChange}
        name="balance"
        placeholder={t("controls.enterAmount")}
        required
      />

      <div className="flex justify-end space-x-4 pt-6 border-t border-ui-border">
        <BoltButton
          label={t("common:actions.save")}
          variant="primary"
          size="lg"
          onClick={handleAddSave}
          disabled={loading}
          loading={loading}
          className="px-8 py-2 text-lg font-semibold min-w-[120px] justify-center"
        />
      </div>
    </div>
  );

  const renderSubtractBalanceTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CustomInput
          label={t("controls.account")}
          value={userData?.username || t("controls.demoUser")}
          readOnly
        />
        <CustomInput
          label={t("controls.reason")}
          value={subtractBalanceData.deduction_reason}
          onChange={handleSubtractChange}
          name="deduction_reason"
          placeholder={t("controls.enterReason")}
          required
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-ui-text mb-2">
          {t("controls.attachmentReason")}
        </label>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors min-h-[140px] flex items-center justify-center ${
            isDragging
              ? "border-brand-hover bg-brand-light"
              : "border-brand bg-brand-light hover:bg-brand-light"
          }`}
        >
          <label htmlFor="file-upload" className="cursor-pointer text-center">
            <Input
              id="file-upload"
              type="file"
              accept="*/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div className="text-center">
              <div className="text-4xl text-brand mb-2">📁</div>
              <Typography variant="h6" color="primary" component="div">
                {subtractBalanceData?.reasonFile === "" ||
                !subtractBalanceData?.reasonFile
                  ? isDragging
                    ? t("controls.dropFileToUpload")
                    : t("controls.dragAndDropImagesHere")
                  : subtractBalanceData?.reasonFile?.name}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                className="mt-2"
              >
                {t("controls.clickToUploadOrDrag")}
              </Typography>
            </div>
          </label>
        </div>
      </div>

      <CustomInput
        label={t("controls.amount")}
        type="number"
        value={subtractBalanceData.balance}
        onChange={handleSubtractChange}
        name="balance"
        placeholder={t("controls.enterAmount")}
        required
      />

      <div className="flex justify-end space-x-4 pt-6 border-t border-ui-border">
        <BoltButton
          label={t("common:actions.save")}
          variant="primary"
          size="lg"
          onClick={handleSubtractSave}
          disabled={loading}
          loading={loading}
          className="py-2 text-lg font-semibold min-w-[120px] justify-center"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Balance Tab Headers */}
      <div className="flex justify-start mb-6">
        <div className="flex">
          <BoltButton
            label={t("controls.addBalanceCaps")}
            variant={activeTab === "add" ? "primary" : "outline"}
            size="md"
            onClick={() => setActiveTab("add")}
            className={`px-6 py-2 rounded-lg transition-all duration-200 ${
              activeTab === "add"
                ? "bg-brand text-white shadow-md"
                : "bg-ui-border text-ui-text-muted hover:bg-ui-border"
            }`}
          />
          <BoltButton
            label={t("controls.subtractBalanceCaps")}
            variant={activeTab === "subtract" ? "primary" : "outline"}
            size="md"
            onClick={() => setActiveTab("subtract")}
            className={`px-6 py-2 rounded-lg transition-all duration-200 ml-3 ${
              activeTab === "subtract"
                ? "bg-brand text-white shadow-md"
                : "bg-ui-border text-ui-text-muted hover:bg-ui-border"
            }`}
          />
        </div>
      </div>

      {/* Balance Tab Content */}
      {activeTab === "add" ? renderAddBalanceTab() : renderSubtractBalanceTab()}
    </div>
  );
};

export default BalanceManagement;
